/**
 * EnvelopeDropIcon — Visuel institutionnel de la zone de dépôt CONFILEX.
 *
 * Concept :
 *   Enveloppe institutionnelle bleu ciel CONFILEX, rabat ouvert légèrement,
 *   deux feuilles blanches qui dépassent par le haut. Sur le rabat, les
 *   trois points officiels CONFILEX (jaune soleil, vert prairie, bleu ciel)
 *   forment une signature visuelle récurrente.
 *
 * Animation :
 *   - Au survol (`group-hover`) : flottement très doux + ouverture
 *     légèrement plus marquée du rabat.
 *   - En drag-over (prop `active` = true) : enveloppe illuminée par un halo,
 *     rabat ouvert, feuilles montent franchement, glow autour du contour.
 *
 * Aucune dépendance externe. SVG pur + CSS variables → s'inscrit dans le
 * design-system institutionnel CONFILEX (cf. ConfilexLogo.jsx — palette
 * `navy / blue / green / yellow / ivory`).
 *
 * Props :
 *   - active  : booléen (true pendant un drag-over → état "déposez ici")
 *   - size    : largeur en px (height auto-déduite via aspect 5/4)
 *   - className : classes additionnelles sur le wrapper
 */
import { BRAND_COLORS } from "@/components/ConfilexLogo";

// Palette harmonisée Vert profond + Or — plus de bleu ciel.
const SKY = "#C9A961";              // or signature (ex bleu ciel)
const NAVY = BRAND_COLORS.navy;     // #0B3A2A — vert sapin profond
const YELLOW = BRAND_COLORS.yellow; // #F5C400
const GREEN = BRAND_COLORS.green;   // #3FA66A
const PAPER = "#FFFFFF";

export default function EnvelopeDropIcon({
  active = false,
  size = 132,
  className = "",
}) {
  const height = Math.round(size * 0.78); // ratio enveloppe institutionnelle

  return (
    <div
      className={`relative inline-block group ${className}`}
      style={{ width: size, height }}
      data-testid="envelope-drop-icon"
      data-active={active ? "true" : "false"}
      aria-hidden="true"
    >
      {/* Halo lumineux en drag-over */}
      <div
        className="absolute inset-0 rounded-2xl pointer-events-none transition-opacity duration-300"
        style={{
          opacity: active ? 0.55 : 0,
          background: `radial-gradient(60% 60% at 50% 55%, ${SKY}55 0%, transparent 70%)`,
          filter: "blur(12px)",
        }}
      />

      <svg
        viewBox="0 0 200 160"
        width={size}
        height={height}
        className="relative"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          overflow: "visible",
          transition: "transform 700ms cubic-bezier(.2,.7,.2,1)",
          transform: active
            ? "translateY(-3px) scale(1.02)"
            : undefined,
        }}
      >
        <defs>
          {/* Dégradé corps de l'enveloppe : vert sapin avec léger relief */}
          <linearGradient id="cfx-env-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#115943" />
            <stop offset="55%" stopColor="#0B3A2A" />
            <stop offset="100%" stopColor="#062618" />
          </linearGradient>
          {/* Dégradé rabat : un peu plus clair pour le distinguer */}
          <linearGradient id="cfx-env-flap" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1A6E54" />
            <stop offset="100%" stopColor="#0B3A2A" />
          </linearGradient>
          {/* Ombre douce sous l'enveloppe */}
          <filter id="cfx-env-shadow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3.5" />
            <feOffset dy="4" result="offsetblur" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.22" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Ombre sur les feuilles pour relief */}
          <filter id="cfx-paper-shadow" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1.2" />
            <feOffset dy="1.5" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.18" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ----- DOS DE L'ENVELOPPE (rectangle arrière, plus sombre) ----- */}
        <rect
          x="22"
          y="58"
          width="156"
          height="86"
          rx="5"
          fill={NAVY}
          opacity="0.10"
          filter="url(#cfx-env-shadow)"
        />

        {/* ----- FEUILLES BLANCHES qui dépassent ----- */}
        <g
          style={{
            transition: "transform 600ms cubic-bezier(.2,.7,.2,1)",
            transform: active
              ? "translateY(-10px)"
              : "translateY(0)",
            transformOrigin: "100px 70px",
          }}
          className="group-hover:[transform:translateY(-4px)]"
          filter="url(#cfx-paper-shadow)"
        >
          {/* feuille du fond (légèrement inclinée à gauche) */}
          <rect
            x="44"
            y="36"
            width="104"
            height="76"
            rx="3"
            fill="#F4F1EA"
            stroke="#D9D2BD"
            strokeWidth="0.8"
            transform="rotate(-3 96 60)"
          />
          {/* feuille du milieu (légèrement inclinée à droite) */}
          <rect
            x="50"
            y="32"
            width="104"
            height="76"
            rx="3"
            fill="#FBF8F1"
            stroke="#E2D9C0"
            strokeWidth="0.8"
            transform="rotate(2 100 60)"
          />
          {/* feuille principale (lignes de texte stylisées) */}
          <g>
            <rect
              x="52"
              y="28"
              width="98"
              height="76"
              rx="3"
              fill={PAPER}
              stroke="#D7CDB1"
              strokeWidth="0.8"
            />
            {/* Lignes "texte" suggérées — abstrait, premium */}
            <rect x="60" y="40" width="58" height="2.2" rx="1" fill="#D7CDB1" />
            <rect x="60" y="48" width="78" height="2.2" rx="1" fill="#E3DBC4" />
            <rect x="60" y="56" width="68" height="2.2" rx="1" fill="#E3DBC4" />
            <rect x="60" y="64" width="80" height="2.2" rx="1" fill="#E3DBC4" />
            <rect x="60" y="72" width="50" height="2.2" rx="1" fill="#E3DBC4" />
            {/* Filigrane CONFILEX (point bleu discret) */}
            <circle cx="138" cy="92" r="2" fill={SKY} opacity="0.45" />
          </g>
        </g>

        {/* ----- CORPS DE L'ENVELOPPE (avant) ----- */}
        <path
          d="M22 80
             L100 130
             L178 80
             L178 140
             Q178 144 174 144
             L26 144
             Q22 144 22 140
             Z"
          fill="url(#cfx-env-body)"
        />
        {/* Liseré supérieur du corps (effet de bord) */}
        <path
          d="M22 80 L100 130 L178 80"
          fill="none"
          stroke={NAVY}
          strokeOpacity="0.18"
          strokeWidth="0.8"
        />

        {/* ----- RABAT DE L'ENVELOPPE (animé) ----- */}
        <g
          style={{
            transformOrigin: "100px 60px",
            transform: active
              ? "rotateX(-32deg)"
              : "rotateX(-14deg)",
            transition: "transform 500ms cubic-bezier(.2,.7,.2,1)",
          }}
          className="group-hover:[transform:rotateX(-22deg)]"
        >
          <path
            d="M22 60
               L100 110
               L178 60
               Q178 56 174 56
               L26 56
               Q22 56 22 60 Z"
            fill="url(#cfx-env-flap)"
            stroke={NAVY}
            strokeOpacity="0.25"
            strokeWidth="0.8"
          />
          {/* Pli central léger pour le relief du rabat */}
          <path
            d="M22 60 L100 110 L178 60"
            fill="none"
            stroke="#FFFFFF"
            strokeOpacity="0.18"
            strokeWidth="0.8"
          />

          {/* ----- 3 POINTS CONFILEX SUR LE RABAT ----- */}
          {/* Disposés en ligne, près du bord supérieur du rabat */}
          <circle cx="86" cy="68" r="3.4" fill={YELLOW} />
          <circle cx="100" cy="68" r="3.4" fill={GREEN} />
          <circle cx="114" cy="68" r="3.4" fill={SKY} />
        </g>

        {/* Glow contour en drag-over */}
        {active && (
          <rect
            x="20"
            y="54"
            width="160"
            height="92"
            rx="6"
            fill="none"
            stroke={SKY}
            strokeWidth="1.6"
            strokeOpacity="0.55"
            style={{ filter: `drop-shadow(0 0 6px ${SKY}88)` }}
          />
        )}
      </svg>
    </div>
  );
}
